// Navbar.tsx
import { useQueryClient } from "@tanstack/react-query";
import Logo from "../../assets/logo.png";
import NavbarLink from "./NavbarLinks/NavbarLink";
import useLogout from "../../state/hooks/auth/useLogout";
import Button from "../Button/Button";
import useMe from "../../state/hooks/auth/useMe";
import { useEffect } from "react";

function Navbar() {
  const { mutate: logout, isPending } = useLogout();
  const { data: userData } = useMe();

  const logOut = () => {
    logout();
  };

  // Define user roles and their permissions
  const isSuperAdmin =
    userData?.data?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "super_admin"
    ) ||
    userData?.user?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "super_admin"
    );
  const isAdmin =
    userData?.data?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "admin"
    ) ||
    userData?.user?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "admin"
    );
  const isTranslator =
    userData?.data?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "translator"
    ) ||
    userData?.user?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "translator"
    );
    const isEducationalContent =
    userData?.data?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "educational_content"
    ) ||
    userData?.user?.roles?.some(
      (role: any) => role?.name?.toLowerCase() === "educational_content"
    );

    console.log(isEducationalContent);
    console.log(userData);

  // Helper function to check if user has access to specific feature
  const hasAccess = (feature: string): boolean => {
    switch (feature) {
      case "translations":
      case "one-time-email":
        return isSuperAdmin || isTranslator;
      case "b2c_users":
      case "b2b_users":
        return isSuperAdmin || isAdmin;
      case "export":
        return isSuperAdmin;
      case "sg_templates":
      case "push_notifications":
        return isSuperAdmin || isAdmin || isTranslator;
      case "prompts":
        return isSuperAdmin || isEducationalContent;
      default:
        return false;
    }
  };

  // Group links by section
  const sections = [
    {
      title: "B2C Management",
      access: "b2c_users",
      links: [
        { to: "/main-users", label: "Users" },
        { to: "/sub-users", label: "Sub users" },
        { to: "/subscriptions", label: "Subscriptions" },
        { to: "/transactions", label: "Transactions" },
      ],
    },
    {
      title: "B2B Management",
      access: "b2b_users",
      links: [
        { to: "/b2b-admins", label: "B2B Admin Users" },
        { to: "/organizations", label: "B2B Organizations" },
        { to: "/b2b-subscriptions", label: "B2B Subscriptions" },
      ],
    },
    {
      title: "Translations",
      access: "translations",
      links: [
        { to: "/translations", label: "App - Translations" },
        { to: "/email-translations", label: "Email - Translations" },
        {
          to: "/notification-translations",
          label: "Notifications - Translations",
        },
        { to: "/landing-translations", label: "Landing - Translations" },
        { to: "/automation", label: "Educational Content - Translations" },
      ],
    },
    {
      title: "Email & Discounts",
      access: "one-time-email",
      links: [
        { to: "/one-time-email", label: "One time email" },
        { to: "/one-time-notifications", label: "One time notifications" },
        { to: "/subscription-discounts", label: "Subscription Discounts" },
      ],
    },
    {
      title: "System",
      access: "prompts",
      links: [{ to: "/prompts", label: "Prompts" }],
    },
    {
      title: "SG Management",
      access: "sg_templates",
      links: [
        { to: "/sg-template-management", label: "SendGrid Templates" },
      ],
    },
    {
      title: "Push Notifications",
      access: "push_notifications",
      links: [
        { to: "/push-notifications", label: "Notifications Management" },
      ],
    },
  ];

  return (
    <div className="h-screen min-w-[280px] max-w-[320px] bg-white flex flex-col py-8 shadow-lg">
      {/* Logo and Title Section */}
      <div className="flex items-center gap-6 px-8 mb-8">
        <img src={Logo} alt="logo" className="w-30" />
        <div className="text-xl font-extrabold">Talkpal</div>
      </div>

      {/* Navbar Links */}
      <nav className="flex-1 flex flex-col gap-6 px-8 overflow-y-auto">
        {sections.map(
          (section, index) =>
            hasAccess(section.access) && (
              <div key={index} className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-gray-500 mb-1">
                  {section.title}
                </h3>
                {section.links.map((link, linkIndex) => (
                  <NavbarLink key={linkIndex} linkTo={link.to}>
                    {link.label}
                  </NavbarLink>
                ))}
              </div>
            )
        )}
      </nav>

      {/* Logout Button */}
      <div className="border-t border-light-grey-1 mt-8 pt-6 px-8">
        <Button
          onClick={logOut}
          disabled={isPending}
          className="text-lg w-full font-semibold py-3 text-red"
          type={isPending ? "secondary" : "main"}
        >
          Logout
        </Button>
      </div>
    </div>
  );
}

export default Navbar;
